import {
  ConfigApi,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  ProjectColumnResponse,
  ProjectRequest,
  ProjectResponse,
  TagRequest,
  Task,
  TaskSearchParams,
  TaskRequest,
  TaskResponse,
  CreateTaskRequest,
  TaskTransactionResponse,
  UserRequest,
  UserResponse,
  UpdateTaskPositionRequest,
  UpdateTaskPositionResponse,
  User,
  UnsubscribeTaskRequest,
  UploadFileRequest,
  SearchFileRequest,
  UploadFileResponse,
  SearchFileResponse,
  ProjectColumnItem,
  UpdatePriorityTaskRequest,
  UpdateTaskTagsRequest,
} from '../types';

type Options = {
  discoveryApi: DiscoveryApi;
  configApi: ConfigApi;
  fetchApi: FetchApi;
};

export const sev2ApiRef = createApiRef<Sev2API>({
  id: 'plugin.sev2.service',
});

// Common literals used across multiple API calls to avoid magic strings.
const JSON_CONTENT_TYPE = 'application/json';
const TRANSACTION_STATUS_TYPE = 'status';
const TRANSACTION_COMMENT_TYPE = 'comment';
const UPDATE_TASK_STATUS_ERROR_PREFIX = 'failed to update task status';
const FETCH_TASK_STATUSES_ERROR_PREFIX = 'failed to fetch task statuses';

export class Sev2API {
  private readonly backendUrl: string | undefined;
  private readonly fetchApi: FetchApi;
  private readonly confApi: ConfigApi;

  constructor(options: Options) {
    this.backendUrl = options.configApi.getOptionalString('backend.baseUrl');
    this.confApi = options.configApi;
    this.fetchApi = options.fetchApi;
  }

  private async getUrls() {
    return {
      apiUrl: `${this.backendUrl}/api/sev2/proxy/`,
    };
  }

  getSev2Url(): string {
    return (
      this.confApi.getOptionalString('integrations.sev2.host') ||
      'https://refactory.sev-2.com'
    );
  }

  async getProject(params: ProjectRequest): Promise<ProjectResponse> {
    const { apiUrl } = await this.getUrls();
    const { projectIds, nameLike } = params;

    let body;

    if (nameLike) {
      // If searching by name
      body = JSON.stringify({
        'constraints[name]': nameLike,
      });
    } else if (Array.isArray(projectIds) && projectIds.length > 0) {
      const constraints: Record<string, string> = {};
      (Array.isArray(projectIds) ? projectIds : []).map((id, index) => {
        constraints[`constraints[ids][${index}]`] = id;
      });
      body = JSON.stringify(constraints);
    } else if (projectIds) {
      body = JSON.stringify({
        'constraints[ids][0]': projectIds,
      });
    } else {
      throw new Error('Either projectIds or nameLike must be provided');
    }

    const request = await this.fetchApi.fetch(`${apiUrl}project.search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  async getProjects(params: ProjectRequest): Promise<ProjectResponse> {
    const { apiUrl } = await this.getUrls();
    const { projectName } = params;

    const reqBodyObj: Record<string, any> = {};
    const reqBodyObjKey = `constraints[nameLike]`;
    reqBodyObj[reqBodyObjKey] = projectName;
    reqBodyObj['constraints[icons][0]'] = ['project'];
    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}project.search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  async getTags(params: TagRequest): Promise<ProjectResponse> {
    const { apiUrl } = await this.getUrls();
    const { tagName } = params;
    const reqBodyObj: Record<string, any> = {};
    const reqBodyObjKey = `constraints[name]`;
    reqBodyObj[reqBodyObjKey] = tagName;
    reqBodyObj['constraints[icons][0]'] = ['tag'];
    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}project.search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  async getTaskByProjectBoard(params: TaskRequest): Promise<TaskResponse> {
    const {
      after = '',
      before = '',
      limit = '10',
      projectBoardUrl = '',
      tagName = '',
    } = params;

    const projectIds = projectBoardUrl.replace(/.*\/(\d+)\/?$/, '$1');

    const project = await this.getProject({
      projectIds: projectIds,
    });

    const tagId = await this.getTags({
      tagName,
    });

    const tagPHID = tagId?.result?.data.find(
      it => it?.fields.name === tagName,
    )?.phid;

    const getTaskPayload = {
      projectPHID: project?.result?.data?.at(0)?.phid,
      tagPHID: tagPHID,
      after,
      before,
      limit,
    };

    const tasks = await this.searchTasks(getTaskPayload);

    if (!tasks) {
      throw new Error(`failed to fetch data, status `);
    }
    return tasks;
  }

  async getAllTaskByProjectBoard(
    params: TaskRequest,
    search: TaskSearchParams | undefined,
  ): Promise<Task[]> {
    const {
      after = '',
      before = '',
      limit = '25',
      projectBoardUrl = '',
      projectTagName = '',
      projectBoardUrls = [],
      projectPHIDs = [],
      queryKey,
      tagPHIDs,
      tagPHID,
    } = params;

    let getTaskPayload = {};

    /** Get project phid */
    if (projectPHIDs.length > 0 || projectBoardUrls.length > 0) {
      let phids = projectPHIDs;
      if (projectBoardUrls.length) {
        const projectIds = projectBoardUrls.map(url => {
          return url.replace(/.*\/(\d+)\/?$/, '$1');
        });
        const projects = await this.getProject({
          projectIds: [...new Set(projectIds)],
        });
        phids = projects.result.data.map(it => it.phid);
      }

      getTaskPayload = {
        projectPHIDs: phids,
        after,
        before,
        limit,
        queryKey: queryKey,
        tagPHIDs: tagPHIDs,
        tagPHID,
      };
    } else {
      let projectPhid = '';
      if (projectBoardUrl) {
        const projectIds: string = projectBoardUrl.replace(
          /.*\/(\d+)\/?$/,
          '$1',
        );

        const project = await this.getProject({
          projectIds: projectIds,
        });
        projectPhid = project?.result?.data?.at(0)?.phid ?? '';
      }

      let tag = tagPHID
      if (projectTagName) {
        const tagId = await this.getTags({
          tagName: projectTagName,
        });

        tag = await tagId?.result?.data.find(
          it => it?.fields.name === projectTagName,
        )?.phid;
      }

      getTaskPayload = {
        projectPHID: projectPhid ? projectPhid : projectTagName,
        tagPHIDs: tagPHIDs,
        after,
        before,
        limit,
        queryKey: queryKey,
        tagPHID: tag,
      };
    }

    const tasks = await this.searchTasks({
      ...getTaskPayload,
      searchParams: search,
    });

    if (!tasks) {
      throw new Error(`failed to fetch data `);
    }

    const mergedTask: Task[] = [...tasks.result.data];
    let nextCursor = tasks.result.cursor?.after;

    while (nextCursor) {
      const nextPayload = { ...getTaskPayload, after: nextCursor }; // Create a new payload with updated cursor
      const nextTasks = await this.searchTasks({
        ...nextPayload,
        searchParams: search,
      });

      if (!nextTasks || !nextTasks.result?.data) {
        throw new Error('Failed to fetch next batch of data');
      }

      mergedTask.push(...nextTasks.result.data);
      nextCursor = nextTasks.result.cursor?.after; // Update the cursor
    }
    return mergedTask;
  }

  async updateTaskPosition(
    params: UpdateTaskPositionRequest,
    headers: Record<string, string>,
  ): Promise<boolean> {
    const { apiUrl } = await this.getUrls();
    const { projectPHID, columnPHID, taskPHID, comments } = params;

    if (typeof comments !== 'undefined') {
      const reqBodyObj: Record<string, any> = {};
      reqBodyObj['transactions[0][type]'] = 'column';
      reqBodyObj['transactions[0][value][0]'] = columnPHID;
      reqBodyObj['transactions[1][type]'] = 'comment';
      reqBodyObj['transactions[1][value]'] = comments;
      reqBodyObj.objectIdentifier = taskPHID;
      const body = JSON.stringify(reqBodyObj);

      const request = await this.fetchApi.fetch(`${apiUrl}maniphest.edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body,
      });

      if (!request.ok) {
        throw new Error(
          `Failed to update task position with comment, status ${request.status}: ${request.statusText}`,
        );
      }

      const response = await request.json();
      return response.error_code === null;
    }

    const body = JSON.stringify({
      projectPHID: projectPHID,
      columnPHID: columnPHID,
      taskPHID: taskPHID,
    });

    const request = await this.fetchApi.fetch(
      `${apiUrl}project.column.maniphest.move`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body,
      },
    );

    if (!request.ok) {
      throw new Error(
        `failed to update task position, status ${request.status}: ${request.statusText}`,
      );
    }

    const response: UpdateTaskPositionResponse = await request.json();

    return response.result.error === false;
  }

  async convertRemarkupToHtml(remarkupText: string): Promise<string> {
    const { apiUrl } = await this.getUrls();

    const reqBodyObj: Record<string, any> = {
      context: 'phriction',
      'contents[0]': remarkupText,
    };

    const response = await this.fetchApi.fetch(`${apiUrl}remarkup.process`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqBodyObj),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to convert Remarkup, status ${response.status}: ${response.statusText}`,
      );
    }

    const data = await response.json();

    if (data.result.length === 0) {
      throw new Error('No content found in the response');
    }

    return data.result[0].content || '';
  }

  async getFeedbackData(projectId?: string): Promise<Task[]> {
    try {
      const { apiUrl } = await this.getUrls();

      const defaultProjectId = 'PHID-PROJ-tzqrxusiw5mhur2vxqae';
      const feedbackProjectId =
        this.confApi.getOptionalString('integrations.sev2.feedbackProjectId') ||
        projectId ||
        defaultProjectId;

      const reqBodyObj = {
        'constraints[projects][0]': feedbackProjectId,
        order: 'newest',
      };

      const request = await this.fetchApi.fetch(`${apiUrl}maniphest.search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reqBodyObj),
      });

      if (!request.ok) {
        return [];
      }

      try {
        const response = await request.json();

        if (!response || !response.result || !response.result.data) {
          return [];
        }

        return response.result.data;
      } catch (jsonError) {
        return [];
      }
    } catch (error) {
      return [];
    }
  }

  async getUser(params: UserRequest): Promise<UserResponse> {
    const { apiUrl } = await this.getUrls();
    const { phids } = params;

    const reqBodyObj: Record<string, any> = {};

    const reqBodyObjKey = `phids[0]`;
    reqBodyObj[reqBodyObjKey] = phids;

    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}user.query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    const data = await request.json();

    if (data.result.length === 0) {
      throw new Error('No content found in the response');
    }

    return data.result[0];
  }

  async getUserByName(params: UserRequest): Promise<UserResponse> {
    const { apiUrl } = await this.getUrls();
    const { name } = params;

    const reqBodyObj: Record<string, any> = {};

    const reqBodyObjConstraintsKey = `constraints[nameLike]`;
    const reqBodyObjOrderKey = `order[0]`;
    reqBodyObj[reqBodyObjConstraintsKey] = name;
    reqBodyObj[reqBodyObjOrderKey] = 'username';

    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}user.search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }

    return request.json();
  }

  async getUserByEmail(email: string): Promise<{ result: User[] }> {
    const { apiUrl } = await this.getUrls();

    const reqBodyObj: Record<string, any> = {};
    reqBodyObj['emails[0]'] = email;
    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}user.query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }

    return request.json();
  }

  async getTaskTransactions(taskId: number): Promise<TaskTransactionResponse> {
    const { apiUrl } = await this.getUrls();

    const reqBodyObj: Record<string, any> = {};

    const reqBodyObjKey = `ids[0]`;
    reqBodyObj[reqBodyObjKey] = taskId;

    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(
      `${apiUrl}maniphest.gettasktransactions`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      },
    );

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    const data = await request.json();

    return data.result[taskId];
  }

  async getProjectColumn(projectPHID: string): Promise<ProjectColumnResponse> {
    const { apiUrl } = await this.getUrls();

    const reqBodyObj: Record<string, any> = {};

    // Handle multiple project PHIDs separated by comma
    if (projectPHID.includes(',')) {
      const projectPHIDs = projectPHID.split(',').map(phid => phid.trim());
      projectPHIDs.forEach((phid, index) => {
        reqBodyObj[`constraints[projects][${index}]`] = phid;
      });
    } else {
      // Handle single project PHID
      reqBodyObj[`constraints[projects][0]`] = projectPHID;
    }

    const body = JSON.stringify(reqBodyObj);
    const request = await this.fetchApi.fetch(
      `${apiUrl}project.column.search`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      },
    );

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    const data = await request.json();

    return data;
  }

  async getProjectColumns(
    projectPHIDs: string[],
  ): Promise<ProjectColumnResponse> {
    if (!projectPHIDs || projectPHIDs.length === 0) {
      throw new Error('At least one project PHID must be provided');
    }

    const { apiUrl } = await this.getUrls();

    const reqBodyObj: Record<string, any> = {};

    projectPHIDs.forEach((phid, index) => {
      const reqBodyObjKey = `constraints[projects][${index}]`;
      reqBodyObj[reqBodyObjKey] = phid;
    });

    const body = JSON.stringify(reqBodyObj);
    const request = await this.fetchApi.fetch(
      `${apiUrl}project.column.search`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      },
    );

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    const data = await request.json();

    return data;
  }

  async getPriorities(): Promise<any> {
    const { apiUrl } = await this.getUrls();

    const request = await this.fetchApi.fetch(
      `${apiUrl}maniphest.priority.search`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // No specific parameters needed for this endpoint
        }),
      },
    );

    if (!request.ok) {
      throw new Error(
        `failed to fetch priorities, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  /**
   * Search for tasks with advanced filtering capabilities
   */
  async searchTasks(
    params: TaskRequest & { searchParams?: TaskSearchParams },
  ): Promise<TaskResponse> {
    const { apiUrl } = await this.getUrls();
    const {
      projectPHID = '',
      tagPHID: tPHID = '',
      projectBoardUrl = '',
      tagName = '',
      searchParams,
      after = '',
      before = '',
      limit = '10',
      queryKey,
      tagPHIDs = [],
    } = params;

    const reqBodyObj: Record<string, any> = {
      'attachments[projects]': '1',
      'attachments[logs]': '1',
      'attachments[subscribers]': '1',
      'attachments[assignments]': '1',
      'constraints[statuses][0]': 'open',
      'constraints[statuses][1]': 'resolved',
      'constraints[statuses][2]': 'wontfix',
      limit: limit,
      before: before,
      after: after,
      queryKey: queryKey,
    };

    // Check if search params include projects
    let idxProject = 0;
    if (searchParams?.projects && searchParams.projects.length > 0) {
      for (let i = 0; i < searchParams.projects.length; i++) {
        reqBodyObj[`constraints[projects][${i}]`] = searchParams.projects[i];
        idxProject += i;
      }
    } else if (projectPHID) {
      reqBodyObj['constraints[projects][0]'] = projectPHID;
      idxProject += 1;
    } else if (projectBoardUrl) {
      // Extract project ID and fetch project data
      const projectIds = projectBoardUrl.replace(/.*\/(\d+)\/?$/, '$1');
      const project = await this.getProject({
        projectIds: projectIds,
      });
      // Add the constraints from the project
      if (project?.result?.data[0]?.phid) {
        reqBodyObj['constraints[projects][0]'] = project.result.data[0].phid;
        idxProject += 1;
      }
    }

    // Get tag PHID if tagName is provided
    let tagPHID = tPHID;
    if (tagName) {
      const tagData = await this.getTags({
        tagName,
      });
      tagPHID =
        tagData?.result?.data.find(
          it => it?.fields.slug.toLowerCase() === tagName.toLowerCase(),
        )?.phid ?? '';
    }

    if (Array.isArray(tagPHIDs) && tagPHIDs.length > 0) {
      for (let i = 0; i < tagPHIDs.length; i++) {
        reqBodyObj[`constraints[projects][${idxProject + i}]`] = tagPHIDs[i];
      }
    } else if (tagPHID) {
      reqBodyObj[`constraints[projects][${idxProject}]`] = tagPHID;
    }

    // Add search constraints if provided
    if (searchParams) {
      // Assigned to
      if (searchParams?.assigned && searchParams.assigned.length > 0) {
        if (
          searchParams.assigned.includes('none()') ||
          searchParams.assigned.includes('anyone()')
        ) {
          reqBodyObj[`constraints[assigned][0]`] = searchParams.assigned[0];
        } else {
          searchParams.assigned.forEach((assignee, index) => {
            reqBodyObj[`constraints[assigned][${index}]`] = assignee;
          });
        }
      }

      // Assigned QA
      if (searchParams.assignedQA && searchParams.assignedQA.length > 0) {
        searchParams.assignedQA.forEach((qa, index) => {
          reqBodyObj[`constraints[assignedQA][${index}]`] = qa;
        });
      }

      // Subtypes
      if (searchParams.subtypes && searchParams.subtypes.length > 0) {
        searchParams.subtypes.forEach((subtype, index) => {
          reqBodyObj[`constraints[subtypes][${index}]`] = subtype;
        });
      }

      // Priorities
      if (searchParams.priorities && searchParams.priorities.length > 0) {
        searchParams.priorities.forEach((priority, index) => {
          reqBodyObj[`constraints[priorities][${index}]`] = priority;
        });
      }

      // Column PHIDs (statuses)
      if (searchParams.columnPHIDs && searchParams.columnPHIDs.length > 0) {
        searchParams.columnPHIDs.forEach((columnPHID, index) => {
          reqBodyObj[`constraints[columnPHIDs][${index}]`] = columnPHID;
        });
      }

      // Date ranges
      if (searchParams.createdStart) {
        reqBodyObj['constraints[createdStart]'] = searchParams.createdStart;
      }
      if (searchParams.createdEnd) {
        reqBodyObj['constraints[createdEnd]'] = searchParams.createdEnd;
      }
      if (searchParams.modifiedStart) {
        reqBodyObj['constraints[modifiedStart]'] = searchParams.modifiedStart;
      }
      if (searchParams.modifiedEnd) {
        reqBodyObj['constraints[modifiedEnd]'] = searchParams.modifiedEnd;
      }
      if (searchParams.closedStart) {
        reqBodyObj['constraints[closedStart]'] = searchParams.closedStart;
      }
      if (searchParams.closedEnd) {
        reqBodyObj['constraints[closedEnd]'] = searchParams.closedEnd;
      }

      // ID/number search
      if (searchParams.idLike) {
        reqBodyObj['constraints[idLike]'] = searchParams.idLike;
      }

      // Title search
      if (searchParams.titleLike) {
        reqBodyObj['constraints[titleLike]'] = searchParams.titleLike;
      }

      // Progress
      if (searchParams.progress) {
        reqBodyObj['constraints[progress]'] = searchParams.progress;
      }

      if (searchParams.query) {
        reqBodyObj['constraints[query]'] = searchParams.query;
      }

      // Order
      if (searchParams.order) {
        reqBodyObj.order = searchParams.order;
      }
    }

    const request = await this.fetchApi.fetch(`${apiUrl}maniphest.search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqBodyObj),
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch data, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  async getTasksByUser(params: TaskSearchParams & { after?: string }): Promise<TaskResponse> {
    const { apiUrl } = await this.getUrls();

    const constraints: Record<string, string | number | undefined> = {
      'constraints[modifiedStart]': params.modifiedStart,
      'constraints[modifiedEnd]': params.modifiedEnd,
      'attachments[projects]': '1',
      'attachments[logs]': '1',
      'attachments[subscribers]': '1',
      'attachments[assignments]': '1',
      'constraints[statuses][0]': 'open',
      'constraints[statuses][1]': 'resolved',
      'constraints[statuses][2]': 'wontfix',
    };

    if (params.assigned?.[0]) {
      constraints['constraints[assigned][0]'] = params.assigned[0];
    }

    if (params.assignedQA?.[0]) {
      constraints['constraints[assignedQA][0]'] = params.assignedQA[0];
    }

    const request = await this.fetchApi.fetch(`${apiUrl}maniphest.search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...constraints,
        after: params.after,
        limit: '10'
      }),
    });

    return request.json();
  }

  async createTicketFeedback(params: CreateTaskRequest): Promise<string> {
    const { apiUrl } = await this.getUrls();

    const defaultProjectId = 'PHID-PROJ-tzqrxusiw5mhur2vxqae';
    const feedbackProjectId =
      this.confApi.getOptionalString('integrations.sev2.feedbackProjectId') ||
      defaultProjectId;

    const columns = await this.getProjectColumn(feedbackProjectId);
    const column = columns?.result?.data?.find(
      (c: ProjectColumnItem) =>
        c?.fields?.name?.toLowerCase().includes('to do') &&
        c.fields?.project?.phid === feedbackProjectId,
    );

    if (column) {
      params.column = column.phid;
    }

    // Ensure projects.set is an array and add feedbackProjectId
    if (!params['projects.set']) {
      params['projects.set'] = [];
    }
    if (Array.isArray(params['projects.set'])) {
      params['projects.set'].push(feedbackProjectId);
    }

    const maniphestRequest: Record<string, any> = {};

    let index = 0;
    Object.entries(params).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && !value.length)) {
        return;
      }
      maniphestRequest[`transactions[${index}][type]`] = key;
      if (Array.isArray(value)) {
        value.forEach((v, i) => {
          maniphestRequest[`transactions[${index}][value][${i}]`] = v;
        });
      } else if (key === 'column') {
        maniphestRequest[`transactions[${index}][value][0]`] = value;
      } else {
        maniphestRequest[`transactions[${index}][value]`] = value;
      }
      index++;
    });
    const body = JSON.stringify(maniphestRequest);

    const orgRes = await this.fetchApi.fetch(`${apiUrl}maniphest.edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
    });
    const response = await orgRes.json();
    return JSON.stringify(response);
  }

  async unsubscribeTask(params: UnsubscribeTaskRequest) {
    const { apiUrl } = await this.getUrls();
    const { taskPHID, userPHID } = params;

    const reqBodyObj: Record<string, any> = {};

    reqBodyObj.objectIdentifier = taskPHID;
    reqBodyObj['transactions[0][type]'] = 'subscribers.remove';
    reqBodyObj['transactions[0][value][0]'] = userPHID;

    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}maniphest.edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to fetch priorities, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  async uploadFile(params: UploadFileRequest): Promise<UploadFileResponse> {
    const { apiUrl } = await this.getUrls();
    const { dataBase64 } = params;

    const reqBodyObj: Record<string, any> = {
      data_base64: dataBase64,
    };
    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}file.upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to upload file, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  async searchFile(params: SearchFileRequest): Promise<SearchFileResponse> {
    const { apiUrl } = await this.getUrls();
    const { filePHID } = params;

    const reqBodyObj: Record<string, any> = {
      "constraints[phids][0]": filePHID,
    };
    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}file.search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to search file, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  async updateTask(params: UpdatePriorityTaskRequest) {
    const { apiUrl } = await this.getUrls();
    const { taskPHID, status, comments, assigneePHID, assigneeQaPHID } = params;

    const reqBodyObj: Record<string, any> = {};
    let index = 0;

    reqBodyObj.objectIdentifier = taskPHID;

    if (status) {
      reqBodyObj[`transactions[${index}][type]`] = 'priority';
      reqBodyObj[`transactions[${index}][value]`] = status;
      index++;
    }

    if (comments) {
      reqBodyObj[`transactions[${index}][type]`] = 'comment';
      reqBodyObj[`transactions[${index}][value]`] = comments;
      index++;
    }

    if (assigneePHID) {
      reqBodyObj[`transactions[${index}][type]`] = 'owner';
      reqBodyObj[`transactions[${index}][value]`] = assigneePHID !== 'none()' ? assigneePHID : '';
      index++;
    }

    if (assigneeQaPHID) {
      reqBodyObj[`transactions[${index}][type]`] = 'ownerQAPHID';
      reqBodyObj[`transactions[${index}][value]`] = assigneeQaPHID !== 'none()' ? assigneeQaPHID : '';
      index++;
    }

    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(`${apiUrl}maniphest.edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
    });

    if (!request.ok) {
      throw new Error(
        `failed to update priorities, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  // Updates a task status and optionally impersonates a user by forwarding their PHID header.
  async updateTaskStatus(
    taskPHID: string,
    status: string,
    userPhid?: string,
    comments?: string,
  ) {
    const { apiUrl } = await this.getUrls();

    const reqBodyObj: Record<string, any> = {
      objectIdentifier: taskPHID,
      'transactions[0][type]': TRANSACTION_STATUS_TYPE,
      'transactions[0][value]': status,
    };

    if (comments) {
      reqBodyObj['transactions[1][type]'] = TRANSACTION_COMMENT_TYPE;
      reqBodyObj['transactions[1][value]'] = comments;
    }

    const body = JSON.stringify(reqBodyObj);

    const request = await this.fetchApi.fetch(
      `${apiUrl}maniphest.edit`,
      {
        method: 'POST',
        headers: {
          'Content-Type': JSON_CONTENT_TYPE,
          ...(userPhid ? { 'X-User-PHID': userPhid } : {}),
        },
        body,
      },
    );

    if (!request.ok) {
      throw new Error(
        `${UPDATE_TASK_STATUS_ERROR_PREFIX}, status ${request.status}: ${request.statusText}`,
      );
    }

    return request.json();
  }
  
  // Adds/removes project tags from a task while preserving existing API headers.
  async updateTaskTags(
    params: UpdateTaskTagsRequest,
    options?: { userPhid?: string },
  ) {
    const { apiUrl } = await this.getUrls();
    
    const projectSearchBody: Record<string, any> = {};
    for(const [index, projectId] of params.projectIds.entries()) {
      projectSearchBody[`constraints[ids][${index}]`] = projectId;
    }

    const projectRequest = await this.fetchApi.fetch(`${apiUrl}project.search`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectSearchBody)
    });
    
    if (!projectRequest.ok) {
      throw new Error(
        `failed to search projects, status ${projectRequest.status}: ${projectRequest.statusText}`,
      );
    }
    
    const projectResponse: ProjectResponse = await projectRequest.json();

    const tagsUpdateBody: Record<string, any> = {};
    tagsUpdateBody.objectIdentifier = params.taskPHID;
    tagsUpdateBody[`transactions[0][type]`] = `projects.${params.type}`;
    for (const [index, project] of projectResponse.result.data.entries()) {      
      tagsUpdateBody[`transactions[0][value][${index}]`] = project.phid;
    }

    const requestHeaders: Record<string, string> = {
      'Content-Type': JSON_CONTENT_TYPE,
    };
    if (options?.userPhid) {
      requestHeaders['X-User-PHID'] = options.userPhid;
    }

    const request = await this.fetchApi.fetch(`${apiUrl}maniphest.edit`, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify(tagsUpdateBody),
    });

    if (!request.ok) {
      throw new Error(
        `failed to update tags, status ${request.status}: ${request.statusText}`,
      );
    }
    return request.json();
  }

  // Retrieves the list of valid task statuses so the UI can build dropdowns/offline caches.
  async getTaskStatuses() {
    const { apiUrl } = await this.getUrls();

    const request = await this.fetchApi.fetch(
      `${apiUrl}maniphest.status.search`,
      {
        method: 'POST',
        headers: {
          'Content-Type': JSON_CONTENT_TYPE,
        },
        body: JSON.stringify({}),
      },
    );

    if (!request.ok) {
      throw new Error(
        `${FETCH_TASK_STATUSES_ERROR_PREFIX}, status ${request.status}: ${request.statusText}`,
      );
    }

    return request.json();
  }
}
